import { TeamUserRoleData } from '@/api';
import { ReactElement, useCallback } from 'react';
import { UIIcon, UIText, UITooltip } from '@/ui';
import { fontColorLight } from '@/ui/styles';
import styles from './team-user-roles.module.scss';
import { useCompaniesTranslation } from '../hooks';

export type TeamUserRolesProps = {
    roleData: TeamUserRoleData[];
    small?: boolean;
};

export function TeamUserRoles(props: TeamUserRolesProps): ReactElement {
    const { roleData, small = false } = props;

    const { t } = useCompaniesTranslation();

    function capitalizeFirstLetter(val: string): string {
        return String(val).charAt(0).toUpperCase() + String(val).slice(1);
    }

    function getTeamRoleName(role: string) {
        if (role === 'admin') {
            return t('shared.roles.team.admin.short_name');
        } else if (role === 'guardian') {
            return t('shared.roles.team.guardian.short_name');
        }
        return capitalizeFirstLetter(role);
    }

    const renderRole = useCallback(
        (roleName: string, index: number, withComma: boolean) => {
            return (
                <UIText
                    key={index}
                    type={'default2'}
                    variant={small ? 'small' : 'basic'}
                >
                    {withComma ? ', ' : ''}
                    {roleName}
                </UIText>
            );
        },
        [small]
    );

    // Plain strings rather than the rich `<Trans>` the role modal uses for the
    // same fact: `UITooltip` takes a `string`, so there is nowhere to hang the
    // bold.
    function getInheritedText(
        role: string,
        inheritedBy?: string | null
    ): string {
        if (inheritedBy) {
            return t('shared.teams.role_inherited_from', {
                companyRole: capitalizeFirstLetter(inheritedBy),
                teamRole: getTeamRoleName(role)
            });
        }

        return t('shared.teams.role_inherited', {
            teamRole: getTeamRoleName(role)
        });
    }

    return (
        <span>
            {roleData.map((role, index) => {
                const isFirst = index === 0;
                if (role.inherited) {
                    return (
                        <UITooltip
                            text={getInheritedText(
                                role.name,
                                role.inheritedFrom
                            )}
                            key={index}
                        >
                            <span>
                                {renderRole(
                                    getTeamRoleName(role.name),
                                    index,
                                    !isFirst
                                )}
                                <UIIcon
                                    name={'info'}
                                    size={12}
                                    color={fontColorLight}
                                    className={styles.inhertIcon}
                                />
                            </span>
                        </UITooltip>
                    );
                } else {
                    return (
                        <span key={index}>
                            {renderRole(
                                getTeamRoleName(role.name),
                                index,
                                !isFirst
                            )}
                        </span>
                    );
                }
            })}
            {roleData.length === 0 && (
                <span>
                    {renderRole(
                        t('shared.roles.team.member.short_name'),
                        0,
                        false
                    )}
                </span>
            )}
        </span>
    );
}
